/**
 * Meta-title A/B testing.
 *
 * Google re-crawls our top pages weekly, so a title test is run on an ISO-week
 * cadence: within one week every visitor and crawler sees the same title, and
 * the variant flips at the week boundary. Search Console then attributes that
 * week's impressions/CTR to exactly one variant, which is what the admin
 * console reports on.
 *
 * Keep variant titles <= 60 characters and globally unique — the title
 * uniqueness audit (`bun run seo:titles`) treats every variant as a live title.
 */
export interface TitleVariant {
  id: "A" | "B";
  title: string;
  description: string;
}

export interface TitleTest {
  path: string;
  /** ISO date the test started — weeks are counted from here. */
  startedAt: string;
  hypothesis: string;
  variants: [TitleVariant, TitleVariant];
}

export const TITLE_TESTS: TitleTest[] = [
  {
    path: "/palworld-games",
    startedAt: "2026-08-27",
    hypothesis: "Leading with 'Free Palworld Games Online' matches head intent; the 'no download' framing may win more clicks from mobile searchers.",
    variants: [
      {
        id: "A",
        title: "Free Palworld Games Online — Play 11 in Browser",
        description:
          "Play 11 free Palworld games in your browser: cake baking sim, base building sim, Pal catching game, TCG memory match, fusion generator, type duel and more.",
      },
      {
        id: "B",
        title: "11 Free Palworld Games — No Download, Play Now",
        description:
          "Eleven free Palworld browser games: bake cakes, build a base, catch Pals, match TCG cards, fuse Pals and drill the type chart. No download needed.",
      },
    ],
  },
  {
    path: "/booster-pack",
    startedAt: "2026-09-07",
    hypothesis: "A snippet that promises a pull rate calculator and tracker utility should out-click a generic simulator title for searchers comparing pack odds.",
    variants: [
      {
        id: "A",
        title: "Palworld Booster Pack Pull Rate Calculator & Tracker",
        description:
          "Free Palworld booster pack pull rate calculator and tracker. Open Dawn of Palpagos packs with real odds, official scans, card grading and 12 free daily packs.",
      },
      {
        id: "B",
        title: "Palworld Booster Pack Odds Tracker — Free Daily Packs",
        description:
          "Track Palworld booster pack pull rates and open free Dawn of Palpagos packs. Real odds, official card scans, gem-mint grading and 12 daily openings.",
      },
    ],
  },

  {
    path: "/map",
    startedAt: "2026-08-24",
    hypothesis: "Leading with 'Interactive' beats leading with 'Fast Travel' for map queries.",
    variants: [
      {
        id: "A",
        title: "Palworld Map: Fast Travel & Alpha Boss Locations",
        description:
          "Interactive Palpagos map: every fast travel point, teleport statue, alpha spawn, merchant and dungeon — filter by level, element or reward and open the guide.",
      },
      {
        id: "B",
        title: "Interactive Palworld Map — Alphas, Statues & Loot",
        description:
          "Filter the full Palpagos map by level, element and reward: alpha spawns, fast travel statues, merchants, dungeons and ore nodes, each linked to its guide.",
      },
    ],
  },
  {
    path: "/bosses",
    startedAt: "2026-08-24",
    hypothesis: "'All Levels' qualifier beats 'Weakness List' for alpha-location queries.",
    variants: [
      {
        id: "A",
        title: "Palworld Boss Map & Weakness List (All Levels)",
        description:
          "Every Palworld boss on one page: alpha locations by level, tower and raid HP, the element that beats each one, counter Pals and drop tables. Updated for 1.0.",
      },
      {
        id: "B",
        title: "Palworld Alpha Boss Locations by Level + Counters",
        description:
          "All alpha, tower and raid bosses with map locations, levels, HP, the element each one is weak to, recommended counter Pals and full drop tables.",
      },
    ],
  },
  {
    path: "/prices",
    startedAt: "2026-09-14",
    hypothesis: "Owning the exact 'Palworld TCG Price chart' query while keeping TCGplayer authority should improve CTR and snippet alignment.",
    variants: [
      {
        id: "A",
        title: "Palworld TCG Price Chart — TCGplayer Values",
        description:
          "Palworld TCG Price Chart for every official printing: TCGplayer market value, completed sales, low and high asks, plus 7-day and 30-day trends.",
      },
      {
        id: "B",
        title: "Palworld TCG Price Chart — Live Card Tracker",
        description:
          "The Palworld TCG Price Chart with daily TCGplayer market prices, real completed sales, price history graphs and links to buy the exact printing.",
      },
    ],
  },
  {
    path: "/tcg-price-index",
    startedAt: "2026-09-14",
    hypothesis: "A question-shaped 'up or down' snippet should beat the benchmark framing for market-direction searches.",
    variants: [
      {
        id: "A",
        title: "Palworld TCG Price Index: Live Card Value Tracker",
        description:
          "The Palworld TCG Price Index tracks the whole card market in one number, with TCGplayer-led 1-day, 7-day and 30-day moves and the biggest movers.",
      },
      {
        id: "B",
        title: "Are Palworld Cards Going Up? TCG Price Index",
        description:
          "See whether Palworld card prices are rising or falling: one market index built from TCGplayer data, with 7-day and 30-day movers updated daily.",
      },
    ],
  },
];

export const TITLE_TEST_BY_PATH: Record<string, TitleTest> = Object.fromEntries(
  TITLE_TESTS.map((t) => [t.path, t]),
);

/** Whole weeks between two ISO dates (UTC, floored). */
export function weekIndex(startedAt: string, at: Date = new Date()) {
  const start = Date.parse(`${startedAt}T00:00:00Z`);
  return Math.max(0, Math.floor((at.getTime() - start) / (7 * 86_400_000)));
}

/** The variant that is live for a path right now (or on a given date). */
export function activeVariant(path: string, at: Date = new Date()): TitleVariant | undefined {
  const test = TITLE_TEST_BY_PATH[path];
  if (!test) return undefined;
  return test.variants[weekIndex(test.startedAt, at) % test.variants.length];
}

/** Monday-anchored ISO date of the test week that contains `at`. */
export function weekStart(startedAt: string, at: Date = new Date()) {
  const start = Date.parse(`${startedAt}T00:00:00Z`);
  const idx = weekIndex(startedAt, at);
  return new Date(start + idx * 7 * 86_400_000).toISOString().slice(0, 10);
}

/** Every variant title, for the uniqueness audit. */
export function allVariantTitles() {
  return TITLE_TESTS.flatMap((t) => t.variants.map((v) => ({ path: t.path, id: v.id, title: v.title })));
}
